import { useRef, useEffect, useState, useCallback } from 'react';
import { SignatureEngine, type SignatureMode } from '../../core/SignatureEngine';
import { useUIStore } from '../../stores/uiStore';
import '../document/Dialogs.css';

interface SignaturePadProps {
  onClose: () => void;
  onSave: (signatureDataUrl: string) => void;
}

export function SignaturePad({ onClose, onSave }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<SignatureEngine | null>(null);
  const [mode, setMode] = useState<SignatureMode>('draw');
  const [typedText, setTypedText] = useState('');
  const [strokeColor, setStrokeColor] = useState('#1a1a2e');

  useEffect(() => {
    if (mode === 'draw' && canvasRef.current) {
      const engine = new SignatureEngine();
      engine.initialize(canvasRef.current);
      engine.setStrokeColor(strokeColor);
      engineRef.current = engine;

      return () => {
        engine.destroy();
        engineRef.current = null;
      };
    }
  }, [mode, strokeColor]);

  const handleClear = useCallback(() => {
    if (engineRef.current) {
      engineRef.current.clear();
    }
    setTypedText('');
  }, []);

  const handleSave = useCallback(() => {
    if (mode === 'draw') {
      if (engineRef.current && !engineRef.current.isEmpty()) {
        onSave(engineRef.current.toDataURL());
        onClose();
      } else {
        useUIStore.getState().addToast({ type: 'warning', message: 'Please draw a signature first' });
      }
    } else if (mode === 'type') {
      if (typedText.trim()) {
        const engine = new SignatureEngine();
        const dataUrl = engine.generateTypedSignature(typedText, 'italic 36px "Dancing Script", cursive', strokeColor);
        onSave(dataUrl);
        onClose();
      } else {
        useUIStore.getState().addToast({ type: 'warning', message: 'Please type your name first' });
      }
    }
  }, [mode, typedText, strokeColor, onSave, onClose]);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            onSave(reader.result);
            onClose();
          }
        };
        reader.readAsDataURL(file);
      }
    },
    [onSave, onClose]
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div className="modal-header">
          <h3>✍️ Add Signature</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="convert-modes" style={{ marginBottom: 'var(--space-4)' }}>
            <button className={`convert-mode-btn ${mode === 'draw' ? 'active' : ''}`} onClick={() => setMode('draw')}>
              <span>✏️ Draw</span>
            </button>
            <button className={`convert-mode-btn ${mode === 'type' ? 'active' : ''}`} onClick={() => setMode('type')}>
              <span>⌨️ Type</span>
            </button>
            <button className={`convert-mode-btn ${mode === 'upload' ? 'active' : ''}`} onClick={() => setMode('upload')}>
              <span>🖼️ Upload</span>
            </button>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            {['#1a1a2e', '#2563eb', '#dc2626', '#16a34a'].map((c) => (
              <button
                key={c}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: c,
                  border: strokeColor === c ? '2px solid var(--color-accent)' : '1px solid var(--color-surface-border)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setStrokeColor(c);
                  engineRef.current?.setStrokeColor(c);
                }}
              />
            ))}
          </div>

          {mode === 'draw' && (
            <div style={{ position: 'relative', border: '1px dashed var(--color-surface-border)', borderRadius: 'var(--radius-md)', background: 'white' }}>
              <canvas ref={canvasRef} width={430} height={160} style={{ display: 'block', cursor: 'crosshair' }} />
              <button className="btn btn-ghost btn-sm" onClick={handleClear} style={{ position: 'absolute', top: 8, right: 8 }}>
                Clear
              </button>
            </div>
          )}

          {mode === 'type' && (
            <div className="input-group">
              <label>Your Name</label>
              <input
                className="input"
                type="text"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder="Type your full name..."
                style={{ fontFamily: '"Dancing Script", cursive, sans-serif', fontSize: '1.5rem' }}
                autoFocus
              />
            </div>
          )}

          {mode === 'upload' && (
            <label className="file-drop-zone">
              <input type="file" accept="image/*" onChange={handleFileUpload} className="sr-only" />
              <span className="file-drop-icon">🖼️</span>
              <span className="file-drop-label">Select signature image (PNG/JPG)</span>
            </label>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {mode !== 'upload' && (
            <button className="btn btn-primary" onClick={handleSave}>
              Add Signature
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
